import axios from "axios";
import settings from "../../configs/appSettings";
import {CurrentUser} from "../CurrentUser";
import moment from "moment";
import { AppInsights } from 'applicationinsights-js';

export const UPLOAD_CANCELLED_BY_USER = "*UPLOAD_CANCELLED_BY_USER_MESSAGE*";

export class HttpService {

    constructor(config) {
        this.config = config;
    }

    static create() {
        const headers = CurrentUser.accessToken ? { "Authorization": `Bearer ${CurrentUser.accessToken}`} : {};

        return new HttpService({
            baseURL: settings.enterpriseApiUrl,
            headers: headers
        });
    }

    static createWithoutAuthorization() {
        return new HttpService({
            baseURL: settings.enterpriseApiUrl
        });
    }

    static createForTransfer() {
        return new HttpService({
            baseURL: settings.authApiUrl,
            headers: {"Authorization": `Bearer ${CurrentUser.accessToken}`}
        });
    }

    requestTransfer(productTarget, additionalRoute) {
        const requestBody = {
            product: productTarget,
            additionalRouteInfo: additionalRoute
        };

        return this.post("/api/transfer", requestBody);
    }

    logoutAndNavigateToLoginPage() {
        CurrentUser.logout();
        window.location.replace(`${settings.authUiUrl}?referrer=${settings.clientId}&returnUrl=${encodeURIComponent(window.location.pathname)}`);
    }

    createAxiosInstance() {
        const instance = axios.create();

        // Add timestamp to GET calls so IE won't cache it
        instance.interceptors.request.use(function (request) {
            if (request.method === 'get') {
                const timestamp = new Date().getTime();

                if (request.url.indexOf("?") === -1) {
                    request.url += "?t=" + timestamp;
                }
                else {
                    request.url += "&t=" + timestamp;
                }
            }

            return request;
        });

        instance.interceptors.response.use(response => {
            return response;
        }, error => {

            // For when a user cancels a file upload
            if (error !== undefined
                    && error.message !== undefined
                    && (error.message.status === 409 || error.message === UPLOAD_CANCELLED_BY_USER)) {
                AppInsights.trackEvent("EnterpriseUI-ConflictResponse", {
                    status: error.message.status,
                    message: error.message.errorMessage
                });
                return;
            }

            if (error === undefined || error.response === undefined) {
                // One way that we can fall into this is if there's a failure response that doesn't have CORS response headers
                // This will cause the browser to fail the request and populate a null response
                AppInsights.trackEvent("EnterpriseUI-ResponseUndefined", { error });

                this.logoutAndNavigateToLoginPage();
                return;
            }

            if (error.response.status === 500) {
                AppInsights.trackEvent("EnterpriseUI-BadRequestResponse", {
                    status: error.response.status,
                    request: error.response.request
                });

                return;
            }

            if (error.response.status === 401 || error.response.status === 400 || error.response.status === 415 ) {
                const originalRequest = error.config;

                try {
                    if (moment(CurrentUser.refreshTokenExpirationDate) < moment()) {
                        this.logoutAndNavigateToLoginPage();
                        return;
                    }
                } catch (e) {
                    this.logoutAndNavigateToLoginPage();
                    return;
                }

                return new Promise((resolve, reject) => {
                    this.tryRefreshToken()
                        .then(() => {
                            originalRequest.headers.Authorization = `Bearer ${CurrentUser.accessToken}`;
                            resolve(axios(originalRequest));
                        })
                        .catch(() => {
                            this.logoutAndNavigateToLoginPage();
                            reject();
                        });
                });
            }

            else {
                AppInsights.trackEvent("EnterpriseUI-5xxResponse", {
                    status: error.response.status,
                    request: error.response.request
                });

                this.logoutAndNavigateToLoginPage();

            }
        });

        return instance;
    }

    get(url, query, axiosConfig) {
        return new Promise((resolve, reject) => {

            const q = query !== undefined ? `?${query}` : "";
            const entireUrl = `${url.trim()}${q}`;

            const c = {...this.config, axiosConfig};
            return this.createAxiosInstance()
                        .get(entireUrl,c)
                        .then(result => resolve(result))
                        .catch(error => reject(error));
        });
    }

    post(url, payload) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .post(url, payload, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }

    put(url, payload) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .put(url, payload, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }

    patch(url, payload) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .patch(url, payload, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }
    delete(url) {
        return new Promise((resolve, reject) => {
            return this.createAxiosInstance()
                .delete(url, this.config)
                .then(result => resolve(result))
                .catch(error => reject(error));
        });
    }

    uploadFile(url, fileObject, progressCallback, cancelTokenSource) {
        return new Promise((resolve, reject) => {

            let baseConfig = this.config;
            baseConfig.onUploadProgress = progressCallback;
            baseConfig.cancelToken = cancelTokenSource.token;

            let data = new FormData();
            data.append('file', fileObject.raw);

            return this.createAxiosInstance()
                        .post(url, data, baseConfig)
                        .then(result => resolve(result))
                        .catch(err => {
                            if (axios.isCancel(err)) {
                                const {message} = err;
                                reject(message === undefined ? "Action canceled." : message);
                            }else {
                                const {message} = err;
                                reject(message === undefined ? "Server error." : message);
                            }
                        });
        });
    }

    createAxiosCancelToken() {
        return axios.CancelToken;
    }

    callApiAnonymously(actionType, url, data) {

        return new Promise((resolve, reject) => {

            return axios({
                        baseURL: settings.enterpriseApiUrl,
                        method: actionType,
                        url: url,
                        headers: {
                            "Content-Type": "application/json"
                        },
                        data: data === null || data !== undefined
                                ? '{}'
                                : JSON.stringify(data)
                    })
                .then(result => resolve(result))
                .catch(error => reject(error));

        });
    }

    tryRefreshToken() {
        return new Promise((resolve, reject) => {
            const requestBody = `grant_type=refresh_token&client_id=${settings.clientId}&refresh_token=${encodeURIComponent(CurrentUser.refreshToken)}`;

            const header = {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                baseURL: settings.authApiUrl
            };

            return this.createAxiosInstance()
                .post("/login", requestBody, header)//todo: add resource for this to api
                .then(result => {
                    const {data} = result;

                    CurrentUser.setTokenInfo(
                        data.access_token,
                        data.refresh_token,
                        data.userId,
                        data.refreshTokenExpirationMinutes,
                        data.expires_in);

                    resolve();
                })
                .catch(error => {
                    reject(error);
                });
        });
    }
}
